import * as Bytescale from '@bytescale/sdk';

(async () => {
  if (!customElements.get('audiogram-form')) {
    class AudiogramForm extends HTMLElement {
      constructor() {
        super();

        // options
        this.options = {
          apiKey: parseJson(this.dataset.options).apiKey,
          accountId: parseJson(this.dataset.options).accountId,
          maxFileSize:
            parseInt(parseJson(this.dataset.options).maxFileSize) * Math.pow(1024, 2) ||
            10 * Math.pow(1024, 2),
          path:
            `/${parseJson(this.dataset.options).path}/{UTC_YEAR}/{UTC_MONTH}/{UTC_DAY}` ||
            '/audiogram/{UTC_YEAR}/{UTC_MONTH}/{UTC_DAY}',
          location: parseJson(this.dataset.options).location,
          onlyWhenTag: parseJson(this.dataset.options).onlyWhenTag === 'true' ? true : false,
          tag: parseJson(this.dataset.options).tag,
        };

        if (!this.options.apiKey && !this.options.accountId) {
          console.warn('Upload API key is required');
          return;
        }

        // upload manager
        this.uploadManager = new Bytescale.UploadManager({
          apiKey: this.options.apiKey,
        });

        this.init();
      }

      init() {
        this.blockSubmit = false;

        // elements
        this.wrapper = this.querySelector('[data-audiogram-form]');
        this.placeholder = this.querySelector('[data-audiogram-form-placeholder]');
        this.currentAudiogram = this.querySelector('[data-current-audiogram]');
        this.inputWrapper = this.querySelector('[data-input-wrapper]');
        this.input = this.querySelector('input[type="file"]');
        this.label = this.querySelector('label');
        this.uploadError = this.querySelector('[data-upload-error]');
        this.fileSizeError = this.querySelector('[data-error-file-size]');
        this.trigger = this.querySelector('[data-file-trigger]');

        this.checkbox = this.querySelector('input[type="checkbox"]');
        this.permissionError = this.querySelector('[data-permission-error]');
        this.submit = this.querySelector('[data-audiogram-button]');

        this.customerInfo = this.dataset.customerInfo ? parseJson(this.dataset.customerInfo) : null;
        if (this.customerInfo) {
          this.options = {
            ...this.options,
            hasAudiogram:
              parseJson(this.dataset.customerInfo).hasAudiogram === 'true' ? true : false,
          };
        }

        this.trigger.addEventListener('click', this.handleTriggerClick.bind(this));
        this.input.addEventListener('change', this.handleFileSelect.bind(this));
        this.submit.addEventListener('click', this.handleUpload.bind(this));

        this.checkbox.addEventListener('change', () => {
          if (this.checkbox.checked) {
            this.permissionError.classList.add('hidden');
          }
        });

        // restore audiogram file info from local storage
        if (!this.options.hasAudiogram) {
          this.restoreAudiogramFileInfo();
        }
      }

      checkAudiogram() {
        const itemWrapper = this.options.location === 'cartPage' ? 'cart-item' : 'quote-item';
        const audiogramRequired = Array.from(document.querySelectorAll(itemWrapper)).some(
          (item) => item.options.audiogram
        );

        if (audiogramRequired) {
          this.wrapper.classList.remove('hidden');
          this.placeholder.classList.add('hidden');
        } else {
          this.wrapper.classList.add('hidden');
          this.placeholder.classList.add('hidden');
        }
      }

      /**
       * Restore the audiogram file info from local storage
       * and update the order attributes
       */
      restoreAudiogramFileInfo() {
        // check if the audiogram is already in local storage
        let audiogram = parseJson(localStorage.getItem('audiogram'));

        if (localStorage.getItem('audiogram') !== null) {
          this.currentAudiogram
            .querySelector('[data-current-audiogram-file')
            .setAttribute('href', audiogram.fileUrl);

          this.currentAudiogram
            .querySelector('[data-current-audiogram-file')
            .setAttribute('title', audiogram.fileName);
          this.currentAudiogram.querySelector('[data-current-audiogram-file').textContent =
            audiogram.fileName;

          this.currentAudiogram.querySelector('[data-current-audiogram-date]').textContent =
            audiogram.fileDate;

          this.querySelector('[data-current-audiogram]').classList.remove('hidden');

          // update order attributes
          this.updateOrderAttributes(audiogram, audiogram.fileDate);
        }
      }

      /**
       * Reset the input file
       */
      resetInput() {
        this.input.value = [];
        this.inputWrapper.classList.remove('file-selected');
      }

      // handle trigger click
      handleTriggerClick() {
        if (this.input.files.length > 0) {
          this.resetInput();
          this.fileSizeError.classList.add('hidden');
        } else {
          this.label.click();
        }
      }

      /**
       * On change, if there is a file selected, show the file name instead of placeholder
       */
      handleFileSelect() {
        this.fileSizeError.classList.add('hidden');
        if (this.input.files.length > 0) {
          this.inputWrapper.classList.add('file-selected');
          this.querySelector('[data-file-name]').textContent = this.input.files[0].name;

          if (this.input.files[0].size > this.options.maxFileSize) {
            this.fileSizeError.classList.remove('hidden');

            this.blockSubmit = true;
          } else {
            this.blockSubmit = false;
          }
        }
      }

      /**
       * Handle the upload
       */
      async handleUpload() {
        if (this.input.files.length > 0) {
          this.uploadError.classList.add('hidden');
          if (!this.checkbox.checked) {
            this.permissionError.classList.remove('hidden');
          } else {
            if (!this.blockSubmit) {
              try {
                this.submit.classList.add('loading');

                const tags = [];

                // if customer info is available, add tags
                this.customerInfo?.firstName ??
                  tags.push(`firstName-${this.customerInfo?.firstName}`);
                this.customerInfo?.lastName ?? tags.push(`lastName-${this.customerInfo?.lastName}`);
                this.customerInfo?.id ?? tags.push(`id-${this.customerInfo?.id}`);

                // set the upload date
                const date = new Date();
                const uploadDate = `${
                  date.getMonth() + 1
                }/${date.getDate()}/${date.getFullYear()} ${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}`;

                await this.uploadManager
                  .upload({
                    // requited params
                    accountId: this.options.accountId,
                    data: this.input.files[0],

                    // optional params
                    tags: tags,

                    // set the path and file name
                    path: {
                      folderPath: this.options.path,
                      fileName: '{UTC_TIME_TOKEN_INVERSE}{UNIQUE_DIGITS_2}{ORIGINAL_FILE_EXT}',
                    },
                  })
                  .then((response) => {
                    // reset input file
                    this.resetInput();

                    // update order attributes
                    this.updateOrderAttributes(response, uploadDate);

                    // update customer fields
                    this.updateCustomerFields(response, uploadDate);

                    // save audiogram file info in local storage
                    this.saveAudiogramFileInfo(response, uploadDate);

                    // restore audiogram file info from local storage
                    this.restoreAudiogramFileInfo();

                    this.submit.classList.remove('loading');
                  });
              } catch (error) {
                this.submit.classList.remove('loading');
                this.uploadError.classList.remove('hidden');
              }
            }
          }
        }
      }

      updateCustomerFields(file, uploadDate) {
        CF.customerReady(function () {
          CF.customer.update({
            audiogram: {
              name: file.originalFileName,
              size: file.size,
              type: file.mime,
              url: file.fileUrl,
            },
            audiogram_upload_date: uploadDate,
            audiogram_url: file.fileUrl,
            audiogram_name: file.originalFileName,
          });
        });
      }

      /**
       * Update the order attributes
       */
      updateOrderAttributes(file, uploadDate) {
        this.querySelector('[data-order-attr-file-name]').value = file.fileName;
        this.querySelector('[data-order-attr-file-url]').value = file.fileUrl;
        this.querySelector('[data-order-attr-file-date]').value = uploadDate;

        // after the order attributes are restores in the audiogram form, update them in the cart page form
        if (this.options.location === 'cartPage') {
          document.querySelector('cart-items form [data-order-attributes]').innerHTML =
            this.querySelector('[data-order-attributes]').innerHTML;
        }
      }

      /**
       * Save the audiogram file info in the local storage
       */
      saveAudiogramFileInfo(file, uploadDate) {
        // check if the audiogram is already in local storage
        let audiogram = parseJson(localStorage.getItem('audiogram'));

        // if it exists, update it
        if (audiogram.length > 0) {
          audiogram.fileName = file.originalFileName;
          audiogram.fileUrl = file.fileUrl;
          audiogram.fileDate = uploadDate;

          localStorage.setItem('audiogram', JSON.stringify(audiogram));
        } else {
          // if it doesn't exist, create it
          audiogram = {
            fileName: file.originalFileName,
            fileUrl: file.fileUrl,
            fileDate: uploadDate,
          };

          localStorage.setItem('audiogram', JSON.stringify(audiogram));
        }
      }
    }

    customElements.define('audiogram-form', AudiogramForm);
  }
})();
